import React from 'react';
import { DataProvider, QDataTable, TableMenuItem, createQColumnHelper } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { ApiKey } from '../../../types';

export type ApiTokensProps = {
  tokens: ApiKey[];
  onDelete: (item: ApiKey) => void;
  onEdit?: (item: ApiKey) => void;
  onRotate: (item: ApiKey) => void;
  showTokenTypeColumn?: boolean;
};

const columnHelper = createQColumnHelper<ApiKey>();

export const ApiTokensList: React.FC<ApiTokensProps> = ({
  tokens,
  onDelete,
  onEdit,
  onRotate,
  showTokenTypeColumn,
}) => {
  const { t } = useTranslation();
  return (
    <DataProvider.Fixed data={tokens}>
      <QDataTable
        columns={[
          {
            header: t('developerApi.table.name'),
            accessorKey: 'name',
          },
          ...(showTokenTypeColumn
            ? [
                columnHelper.status(
                  (token) => (token.is_real_user ? t('developerApi.table.personal') : t('developerApi.table.system')),
                  {
                    id: 'is_real_user',
                    header: t('developerApi.table.type'),
                    statuses: {
                      [t('developerApi.table.personal')]: 'blue',
                      [t('developerApi.table.system')]: 'gray',
                    },
                    tooltip: (token) =>
                      token.is_real_user
                        ? t('developerApi.table.personalTooltip')
                        : t('developerApi.table.systemTooltip'),
                  },
                ),
              ]
            : []),
          columnHelper.date((token) => new Date(token.created_at * 1000), {
            id: 'created_at',
            header: t('developerApi.table.created'),
            enableSorting: true,
          }),
          columnHelper.date((token) => new Date(token.expires_at * 1000), {
            id: 'expires_at',
            header: t('developerApi.table.expires'),
            highlightPastDates: true,
            enableSorting: true,
          }),
          columnHelper.menu({
            items: (
              <>
                {onEdit && (
                  <TableMenuItem
                    data-cy="edit-key"
                    onClick={(item: ApiKey) => onEdit(item)}
                    isHidden={(item: ApiKey) => !!showTokenTypeColumn && item.is_real_user}
                  >
                    {t('common.edit')}
                  </TableMenuItem>
                )}
                <TableMenuItem
                  data-cy="rotate-key"
                  onClick={(item: ApiKey) => onRotate(item)}
                  isHidden={(item: ApiKey) => !!showTokenTypeColumn && item.is_real_user}
                >
                  {t('developerApi.table.rotate')}
                </TableMenuItem>
                <TableMenuItem data-cy="delete-key" isDestructive onClick={(item: ApiKey) => onDelete(item)}>
                  {t('common.delete')}
                </TableMenuItem>
              </>
            ),
          }),
        ]}
      />
    </DataProvider.Fixed>
  );
};
